import { useState } from 'react';
import { useLang, useT } from '../i18n';
import { useLibrary } from '../state/library';
import { useUi } from '../state/ui';
import { openPhotos, openRecent } from '../io/open';
import { recentFolders, type RecentFolder } from '../io/fs';
import { MetadataDialog } from '../ui/dialogs/MetadataDialog';
import { useSimple } from '../state/simple';
import { useWorkspace } from '../state/workspace';
import s from './Shell.module.css';
import { makeAdFromPhotos } from '../design/ad/actions';
import { useAiUi } from '../features/ai/aiActions';
import { useAi } from '../features/ai/aiSettings';

export function TopBar() {
  const t = useT();
  const [metaOpen, setMetaOpen] = useState(false);
  const mode = useSimple((st) => st.mode);
  const { lang, setLang } = useLang();
  const folder = useLibrary((st) => st.folderName);
  const count = useLibrary((st) => st.photos.length);
  const ui = useUi();
  const [recent, setRecent] = useState<RecentFolder[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  // Simple mode shows only what a first-time user needs (CODE_REVIEW S18): the Pro tools (folders, zoom,
  // split, clipping, cull, the full export dialog, metadata) stay behind the Pro switch.
  const pro = mode === 'pro';
  const ws = useWorkspace((st) => st.ws);
  const designEnabled = useWorkspace((st) => st.designEnabled);
  const photoTools = pro && ws === 'photos';
  const aiOn = useAi((a) => !!a.settings?.key);
  return (
    <header className={s.top}>
      <span className={s.title}>{t('appName')}</span>
      {/* Photos / Design (DRAW_PLAN G1): shown in both modes, plain words — only while Design is turned on
          (paused by the owner; off by default, see state/workspace.ts). */}
      {
        <div className={s.langToggle} role="group" aria-label={t('workspace')}>
          <button
            aria-pressed={ws === 'photos'}
            data-testid="ws-photos"
            onClick={() => useWorkspace.getState().setWs('photos')}
          >
            {t('wsPhotos')}
          </button>
          {designEnabled && (
            <button
              aria-pressed={ws === 'design'}
              data-testid="ws-design"
              onClick={() => useWorkspace.getState().setWs('design')}
            >
              {t('wsDesign')}
            </button>
          )}
          <button
            aria-pressed={ws === 'plating'}
            data-testid="ws-plating"
            onClick={() => useWorkspace.getState().setWs('plating')}
          >
            {t('wsPlating')}
          </button>
        </div>
      }
      {pro && (
        <>
          <button onClick={() => void openPhotos()} data-testid="open-folder">
            {t('openFolder')}
          </button>
          <span style={{ position: 'relative' }}>
            <button
              aria-expanded={!!recent}
              onClick={async () => setRecent(recent ? null : await recentFolders())}
            >
              {t('recent')}
            </button>
            {recent && (
              <ul className={s.menu} role="menu">
                {recent.length === 0 && <li className={s.menuEmpty}>{t('recentEmpty')}</li>}
                {recent.map((r) => (
                  <li key={r.name + r.openedAt}>
                    <button
                      role="menuitem"
                      onClick={async () => {
                        setRecent(null);
                        if (!(await openRecent(r))) setMsg(t('permissionNeeded', { name: r.name }));
                      }}
                    >
                      {r.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </span>
          <span className={s.folder}>
            {count
              ? `${folder ?? t('openFiles')} · ${t('photoCount', { n: count })}`
              : (msg ?? t('noFolder'))}
          </span>
        </>
      )}
      {photoTools && (
        <>
          <button
            aria-pressed={ui.zoom === 1}
            onClick={() => ui.set({ zoom: ui.zoom === 1 ? 'fit' : 1 })}
            title="Z"
          >
            {ui.zoom === 1 ? t('zoomFit') : t('zoom100')}
          </button>
          <button aria-pressed={ui.split} onClick={() => ui.set({ split: !ui.split })} title="Y">
            {t('split')}
          </button>
          <button
            aria-pressed={ui.clipping}
            onClick={() => ui.set({ clipping: !ui.clipping })}
            title="J"
            data-testid="clipping"
          >
            {t('clipping')}
          </button>
          <button disabled={!count} onClick={() => useLibrary.getState().set({ cull: true })} title="E / F">
            {t('cullView')}
          </button>
        </>
      )}
      <span className={s.spacer} />
      <button
        data-testid="open-ai"
        onClick={() => useAiUi.setState({ settingsOpen: true })}
        title={t('aiSettings')}
        aria-pressed={aiOn}
      >
        ✨ {t('aiButton')}
      </button>
      <label className={s.proSwitch}>
        <input
          type="checkbox"
          role="switch"
          data-testid="pro-mode"
          checked={mode === 'pro'}
          onChange={(e) => useSimple.getState().setMode(e.target.checked ? 'pro' : 'simple')}
        />
        {t('proMode')}
      </label>
      {photoTools && (
        <>
          <button
            disabled={!count}
            onClick={() => ui.set({ exportOpen: true })}
            title="Ctrl+E"
            data-testid="open-export"
          >
            {t('export')}
          </button>
          <button onClick={() => setMetaOpen(true)} data-testid="open-metadata">
            {t('metadataButton')}
          </button>
          {designEnabled && (
            <button onClick={makeAdFromPhotos} data-testid="topbar-make-ad" title={t('makeAdHint')}>
              {t('makeAd')}
            </button>
          )}
        </>
      )}
      {metaOpen && <MetadataDialog onClose={() => setMetaOpen(false)} />}
      <div className={s.langToggle} role="group" aria-label={t('language')}>
        <button aria-pressed={lang === 'en'} onClick={() => setLang('en')}>
          EN
        </button>
        <button aria-pressed={lang === 'es'} onClick={() => setLang('es')}>
          ES
        </button>
      </div>
      <button
        onClick={() => window.open(`/help/${lang}/index.html`, '_blank', 'noopener')}
        data-testid="open-help"
      >
        {t('help')}
      </button>
      <button onClick={() => ui.set({ cheatOpen: true })} title="?" aria-label={t('cheatTitle')}>
        ⌨
      </button>
      <button onClick={() => ui.set({ aboutOpen: true })} data-testid="open-about">
        {t('about')}
      </button>
    </header>
  );
}
